import * as React from 'react';
import { ApolloConsumer } from 'react-apollo';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText
} from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';

const DropzoneS3Uploader = require('react-dropzone-s3-uploader');

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import { uploadRequest, kmzParse } from '../services';

const styles: StyleRulesCallback = theme => ({
  dropzone: {
    width: '600px !important',
    height: '160px !important',
    border: 'none !important',
    background: '#EEE',
    borderRadius: '2px !important',
    padding: '30px 0 !important',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dropzoneLabel: {
    flex: 0,
    margin: '10px 0',
    fontSize: 18
  },
  dropzoneIcon: {
    flex: 1,
    fontSize: 64
  },
  progress: {
    width: 600,
    height: 160,
    textAlign: 'center',
    paddingTop: 30
  },
  table: {
    width: 600
  }
});

interface Props {
  onClose: () => void;
  onSubmit: (fields: TempField[]) => void;
  open: boolean;
}

interface TempField {
  id: string;
  name: string;
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  currentCrop?: string;
  area: number;
}

interface State {
  uploaded: boolean;
  loading: boolean;
  fields: TempField[];
  selectedFields: string[];
}

class UploadKmzDialog extends React.Component<Props & WithStyles, State> {
  constructor(props: Props & WithStyles) {
    super(props);
    this.state = {
      uploaded: false,
      loading: false,
      fields: [],
      selectedFields: []
    };
  }

  handleClose = () => {
    this.props.onClose();
  };

  getSignedUrl = (client: any, file: File, callback: (params: any) => void) => {
    uploadRequest(client, file).then(callback);
  };

  handleFileUploaded = async (client: any, result: any) => {
    this.setState({ uploaded: true, loading: true });
    const results = await kmzParse(client, result.fileUrl);
    let fields = results.map((f: any, index: number) => ({ ...f, id: `field-${index}` })) as TempField[];
    this.setState({
      loading: false,
      fields: fields,
      selectedFields: fields.map(f => f.id)
    });
  };

  handleChangeFieldName = (
    event: any,
    field: TempField
  ) => {
    event.preventDefault();
    this.setState({
      fields: this.state.fields.map(f =>
        f.id !== field.id ? f : { ...f, name: event.currentTarget.value }
      )
    });
  };

  handleChangeFieldCrop = (
    event: any,
    field: TempField
  ) => {
    event.preventDefault();
    this.setState({
      fields: this.state.fields.map(f =>
        f.id !== field.id ? f : { ...f, currentCrop: event.currentTarget.value }
      )
    });
  };

  handleToggleField = (field: TempField) => {
    const { selectedFields } = this.state;
    if (selectedFields.find(id => id === field.id)) {
      this.setState({
        selectedFields: selectedFields.filter(id => id !== field.id)
      });
    } else {
      this.setState({
        selectedFields: [ ...selectedFields, field.id ]
      });
    }
  };

  handleSubmit = () => {
    this.props.onSubmit(
      this.state.fields.filter(f =>
        this.state.selectedFields.indexOf(f.id) > -1
      )
    );
  };

  renderDropzone(client: any) {
    const { classes } = this.props;

    return (
      <DropzoneS3Uploader
        s3Url="https://s3-us-west-2.amazonaws.com/pirwa-cache/kmz"
        className={classes.dropzone}
        multiple={false}
        upload={{
          getSignedUrl: (file: any, callback: any) =>
            this.getSignedUrl(client, file, callback),
            uploadRequestHeaders: { 'x-amz-acl': 'public-read' }
        }}
        accept="application/vnd.google-earth.kmz,application/vnd.google-earth.kml+xml"
        onFinish={(result: any) => this.handleFileUploaded(client, result)}
      >
        <CloudUploadIcon className={classes.dropzoneIcon} />
        <span className={classes.dropzoneLabel}>
          Seleccione o arrastre el archivo aquí
        </span>
      </DropzoneS3Uploader>
    );
  }

  renderTable() {
    const { classes } = this.props;
    const { loading, fields, selectedFields } = this.state;

    if (loading) {
      return (
        <div className={classes.progress}>
          <CircularProgress size={100} />
        </div>
      );
    }

    return (
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Nombre de lote</TableCell>
            <TableCell>Cultivo</TableCell>
            <TableCell numeric={true}>Área</TableCell>
            <TableCell padding="checkbox" />
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field: TempField, index: number) => {
            return (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    fullWidth={true}
                    onChange={e => this.handleChangeFieldName(e, field)}
                    value={field.name}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth={true}
                    onChange={e => this.handleChangeFieldCrop(e, field)}
                    value={field.currentCrop}
                  />
                </TableCell>
                <TableCell numeric={true}>{field.area}</TableCell>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedFields.indexOf(field.id) > -1}
                    onClick={() => this.handleToggleField(field)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  render() {
    const { open } = this.props;
    const { uploaded, selectedFields } = this.state;

    return (
      <ApolloConsumer>
        {client => (
          <Dialog
            open={open}
            onClose={this.props.onClose}
            onBackdropClick={this.props.onClose}
            onEscapeKeyDown={this.props.onClose}
            maxWidth={false}
          >
            <DialogTitle>Importar Archivo KML/KMZ</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {!uploaded ? this.renderDropzone(client) : this.renderTable()}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.props.onClose}>Cancelar</Button>
              <Button
                disabled={!selectedFields.length}
                onClick={this.handleSubmit}
              >
                Importar
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </ApolloConsumer>
    );
  }
}

export default withStyles(styles)(UploadKmzDialog);



// WEBPACK FOOTER //
// ./src/components/UploadKmzDialog.tsx